import { Formik, getIn } from "formik";
import { Button, Card, Col, Row, Spinner } from "reactstrap";
import { validationSchema } from "../../../validations/pickList.validation";
import FormInput from "../../../components/FormInput";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import QueryString from "qs";
import { toast } from "react-hot-toast";
import { addPickList, getPickList, updatePickList } from "../../../services/pickList.service";
import FormSelect from "../../../components/FormSelect";
import { getParties } from "../../../services/parties.service";
import React, { useContext, useEffect, useRef, useState } from "react";
import moment from "moment";
import { CompanyContext } from "../../../contexts/CompanyContext";

interface Party {
  id: string;
}

interface InitialValues {
  party: Party | undefined;
  orderNumber: number | undefined;
  date: any;
  orderAmount: number | undefined;
  lineItem: number | undefined;
  VOR: false;
  U: false;
}

const initialValues: InitialValues = {
  party: undefined,
  orderNumber: undefined,
  date: moment().format("YYYY-MM-DD"),
  orderAmount: undefined,
  lineItem: undefined,
  VOR: false,
  U: false,
};

export default function Form() {
  const location = useLocation();
  const navigate = useNavigate();
  const params: any = useParams();
  const [initialValuesData, setInitialValuesData] = useState(initialValues);
  const [loader, setLoader] = useState(true);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const query: any = QueryString.parse(location.search, { ignoreQueryPrefix: true });
  const { selectedCompany }: any = useContext(CompanyContext);
  useEffect(() => {
    if (params.id) {
      getPickList(params.id)
        .then((response) => {
          const picklistData = response.data;

          // Update the initialValues object with the received data
          const updatedInitialValues = {
            ...initialValues,
            party: picklistData.party || undefined,
            orderNumber: picklistData.orderNumber || null,
            date: picklistData.date
              ? moment(picklistData.date).format("YYYY-MM-DD")
              : moment().format("YYYY-MM-DD"),
            orderAmount: picklistData.orderAmount || null,
            lineItem: picklistData.lineItem || null,
            VOR: picklistData.VOR || false,
            U: picklistData.U || false,
          };

          setInitialValuesData(updatedInitialValues);
          setLoader(false);
        })
        .catch((error) => {
          // Handle error if API call fails

          toast.error(`Error fetching picklist data:${JSON.stringify(error)}`);
        });
    } else {
      setLoader(false);
    }
  }, [params.id]);

  function handleSubmit(values: any, actions: any) {
    // Check if file is required for companies with import enabled (only in add case)
    if (selectedCompany?.isAllowPicklistImport && !params.id && !selectedFile) {
      toast.error("File is required for pick list creation when import is enabled");
      actions.setSubmitting(false);
      return;
    }

    // Create FormData if file is selected, otherwise use regular JSON
    if (selectedFile) {
      // Check if company allows picklist import
      if (!selectedCompany?.isAllowPicklistImport) {
        toast.error("Pick list import is not enabled for this company");
        actions.setSubmitting(false);
        return;
      }

      const formData = new FormData();
      formData.append("file", selectedFile);

      // Add other form data to FormData
      formData.append("party", values.party.id);
      formData.append("orderNumber", values.orderNumber);
      formData.append("date", values.date);
      formData.append("orderAmount", values.orderAmount);
      formData.append("lineItem", values.lineItem);
      formData.append("VOR", values.VOR);
      formData.append("U", values.U);

      // Send as FormData for file upload
      addPickList(formData)
        .then((response) => {
          toast.success(response.data.message);

          if (response.data.data?.pdfDownloadUrl) {
            window.open(response.data.data.pdfDownloadUrl, "_blank");
          }

          navigate(query.redirect);
        })
        .catch((error) => {
          console.error("Import error:", error);
          const errorMessage = error.response?.data?.message || "Failed to import file";

          // Provide specific guidance for common errors
          if (
            errorMessage.includes("corrupted data") ||
            errorMessage.includes("invalid characters")
          ) {
            toast.error(
              "File contains corrupted data. Please save your CSV file in UTF-8 encoding and try again.",
            );
          } else if (errorMessage.includes("Missing required columns")) {
            toast.error(
              "File is missing required columns. Please check the file format and try again.",
            );
          } else if (errorMessage.includes("empty") || errorMessage.includes("no data")) {
            toast.error(
              "File appears to be empty or contains no readable data. Please check the file and try again.",
            );
          } else {
            toast.error(errorMessage);
          }

          actions.setSubmitting(false);
        });
    } else {
      // Regular form submission
      const updatedData: any = {
        ...values,
        party: values.party.id,
      };

      if (params.id) {
        updatePickList(params.id, updatedData)
          .then((response: any) => {
            toast.success(response.data.message);
            navigate(query.redirect);
          })
          .catch(() => {
            actions.setSubmitting(false);
          });
      } else {
        addPickList(updatedData)
          .then((response) => {
            toast.success(response.data.message);
            navigate(query.redirect);
          })
          .catch(() => {
            actions.setSubmitting(false);
          });
      }
    }
  }

  const handleFileUpload = () => {
    // Check if company allows picklist import
    if (!selectedCompany?.isAllowPicklistImport) {
      toast.error("Pick list import is not enabled for this company");
      return;
    }

    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Double-check company permission
    if (!selectedCompany?.isAllowPicklistImport) {
      toast.error("Pick list import is not enabled for this company");
      return;
    }

    const file = event.target.files?.[0];
    if (file) {
      const allowedTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
        "text/csv",
      ];

      if (!allowedTypes.includes(file.type)) {
        toast.error("Please select a valid Excel (.xlsx, .xls) or CSV file");
        return;
      }

      // Check file size (limit to 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (file.size > maxSize) {
        toast.error("File size must be less than 10MB");
        return;
      }

      // Save the file in state variable
      setSelectedFile(file);
      toast.success(`File "${file.name}" selected successfully. Click Submit to import.`);

      // Reset the input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <div className='d-flex flex-column h-100 px-sm-1 w-100 pb-sm-1'>
      {loader ? (
        <Spinner />
      ) : (
        <Formik
          initialValues={initialValuesData}
          validationSchema={validationSchema(selectedCompany)}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            setFieldValue,
            handleSubmit,
            isSubmitting,
          }) => (
            <>
              <Row className='align-items-center mb-2'>
                <Col sm>
                  <h1 className='my-1 fw-bold h4'>Pick List</h1>
                </Col>
                <Col sm='auto'>
                  <div className='justify-content-between'>
                    <Button
                      size='sm'
                      color='secondary'
                      className='my-1 text-white me-2'
                      onClick={() => {
                        navigate(query.redirect);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size='sm'
                      color='primary'
                      className='my-1 text-white'
                      onClick={() => handleSubmit()}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Spinner size='sm' className='me-2' />
                          Processing...
                        </>
                      ) : (
                        "Submit"
                      )}
                    </Button>
                  </div>
                </Col>
              </Row>
              <div className='flex-grow-1 overflow-auto'>
                <Card className='h-100 p-3 w-100 '>
                  <Col md={8}>
                    <div className='d-flex flex-column h-100 rounded-0'>
                      <form onSubmit={handleSubmit}>
                        <Row>
                          <Col md={6}>
                            <FormSelect
                              id='party'
                              name='party'
                              label='Party'
                              isAsync
                              defaultFilter={{ status: true }}
                              getOptions={getParties}
                              onChange={(name: any, value: any) => {
                                setFieldValue(name, value);
                              }}
                              onBlur={handleBlur}
                              value={getIn(values, "party")}
                              error={getIn(errors, "party")}
                              touched={getIn(touched, "party")}
                              getOptionLabel={(row: any) => (
                                <>
                                  <div>{row.code}</div>
                                  <small>{row.name}</small>
                                </>
                              )}
                            />
                          </Col>
                          <Col md={6}>
                            <FormInput
                              name='orderNumber'
                              label='Order Number'
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.orderNumber}
                              error={errors.orderNumber}
                              touched={touched.orderNumber}
                            />
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <FormInput
                              name='date'
                              label='Date'
                              type='date'
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.date}
                              error={errors.date}
                              touched={touched.date}
                            />
                          </Col>
                          <Col md={6}>
                            <FormInput
                              name='orderAmount'
                              label='Order Amount'
                              type='number'
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.orderAmount}
                              error={errors.orderAmount}
                              touched={touched.orderAmount}
                            />
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <FormInput
                              name='lineItem'
                              label='Line Item'
                              type='number'
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.lineItem}
                              error={errors.lineItem}
                              touched={touched.lineItem}
                            />
                          </Col>
                          <Col md={6}>
                            <div className='d-flex mt-4 pt-2'>
                              <div className='form-check me-4'>
                                <input
                                  type='checkbox'
                                  className='form-check-input'
                                  id='VOR'
                                  name='VOR'
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  checked={values.VOR}
                                />
                                <label className='form-check-label' htmlFor='VOR'>
                                  VOR
                                </label>
                              </div>
                              <div className='form-check'>
                                <input
                                  type='checkbox'
                                  className='form-check-input'
                                  id='U'
                                  name='U'
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  checked={values.U}
                                />
                                <label className='form-check-label' htmlFor='U'>
                                  U
                                </label>
                              </div>
                              {selectedCompany?.isAllowPicklistImport && !params.id && (
                                <Button
                                  size='sm'
                                  color='success'
                                  className='text-white ms-3'
                                  onClick={handleFileUpload}
                                >
                                  Import Excel/CSV <span className='text-danger'>*</span>
                                </Button>
                              )}
                              {selectedFile && !params.id && (
                                <Button
                                  size='sm'
                                  color='danger'
                                  className='text-white ms-2'
                                  onClick={() => setSelectedFile(null)}
                                >
                                  Clear File
                                </Button>
                              )}
                            </div>
                            {selectedFile && !params.id && (
                              <div className='mt-2'>
                                <small className='text-success'>
                                  Selected file: {selectedFile.name}
                                </small>
                              </div>
                            )}
                            {selectedCompany?.isAllowPicklistImport &&
                              !params.id &&
                              !selectedFile && (
                                <div className='mt-2'>
                                  <small className='text-danger'>
                                    * File is required for pick list creation
                                  </small>
                                </div>
                              )}
                          </Col>
                        </Row>
                      </form>
                    </div>
                  </Col>
                </Card>
              </div>

              {/* Hidden file input */}
              <input
                type='file'
                ref={fileInputRef}
                style={{ display: "none" }}
                accept='.xlsx,.xls,.csv'
                onChange={handleFileChange}
              />
            </>
          )}
        </Formik>
      )}
    </div>
  );
}
